import { FeeRange, NextFeeLevelInfo } from '../models';

/**
 * Calculates the fee level based on the usage count and fee ranges.
 * @param usageCount The number of times the fee has been used.
 * @param feeRanges The array of fee ranges to search through.
 * @returns The index of the fee level.
 */
export function calculateFeeLevel(usageCount: number, feeRanges: FeeRange[]): number {
  if (!feeRanges.length) return 0;
  const index = feeRanges.findIndex(range => usageCount >= (range.min || 0) && usageCount <= (range.max || Infinity));
  return index !== -1 ? index : 0;
}

/**
 * Calculates the fee amount based on the fee ranges, level, and fee level.
 * @param feeRanges The array of fee ranges.
 * @param level The current fee level index.
 * @param feeLevel The specific fee level to calculate the amount for.
 * @returns The fee amount.
 */
export function calculateFeeAmount(feeRanges: FeeRange[], level: number, feeLevel: string): number {
  const feeByLevel = feeRanges[level]?.feeByLevel as any;
  return feeByLevel?.[feeLevel] || 0;
}

/**
 * Calculates the next fee level information based on the usage count, fee ranges, and current fee level.
 * @param usageCount The number of times the fee has been used.
 * @param feeRanges The array of fee ranges.
 * @param feeLevel The current fee level.
 * @returns The next fee level information or null if already at the maximum level.
 */
export function calculateNextFeeLevel(usageCount: number, feeRanges: FeeRange[], feeLevel: string): NextFeeLevelInfo | null {
  if (!feeRanges.length || !feeLevel) return null;
  for (let i = 0; i < feeRanges.length; i++) {
    if (usageCount <= feeRanges[i].max) {
      const nextRange = feeRanges[i + 1];
      if (!nextRange) return null;
      return {
        fee: (nextRange.feeByLevel as any)[feeLevel],
        usesNeeded: feeRanges[i].max + 1 - usageCount,
        level: i + 1,
      };
    }
  }
  return null; // Already at the maximum level
}
